//#region redux
import { useDispatch } from "react-redux";
import { displayDialog } from "store/actions/utilActions";
import { DialogProps } from "models/propTypes/dialogPropTypes";
//#endregion
//#region types
import { DialogEnums, StyleEnums, WintSalaryEnums } from "models/enums";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants } from "utils/constants";
import { DateRange } from "models/formTypes/util";
import { WintSuperkollAPIModelWintSalary } from "wint-api-client/dist/types";
//#endregion
//#region components
import SalaryDeviationHelper from "./forms/salaryDeviationHelper";
import DeleteForm from "./forms/deleteForm";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

function useDeleteConfirmation() {
  const dispatch = useDispatch();

  const deleteConfirmationDialog = (
    onDelete: () => Promise<any>,
    onContinueEditing?: () => Promise<any>
  ): DialogProps => ({
    isOpen: false,
    title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.DeleteDeviationQuestion),
    titleIcon: IconDefinitionConstants.WTrashAlt,
    actions: [
      {
        text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Delete),
        onClick: onDelete,
        type: StyleEnums.DisplayType.Danger,
        successMessage: LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.SalaryDeviation.DeleteDeviationSuccess
        ),
      },
    ],
    hideCloseButton: true,
    dialogType: DialogEnums.DialogType.Message,
    children: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.DeleteWarningDeviation),
    type: StyleEnums.DisplayType.Danger,
    onClose: onContinueEditing,
  });

  const deleteFormDialog = (
    deviationId: string,
    openedPeriod?: DateRange,
    selectedPeriod?: Date,
    showAllDeleteOption?: boolean,
    closeDialog?: () => void,
    onDeleteSuccess?: (result?: WintSuperkollAPIModelWintSalary.SalaryMonthMutationResultBase) => void
  ): DialogProps => {
    const selectedPeriodRange: DateRange | undefined = selectedPeriod
      ? {
          start: new Date(selectedPeriod.getFullYear(), selectedPeriod.getMonth(), 1),
          end: new Date(selectedPeriod.getFullYear(), selectedPeriod.getMonth() + 1, 0),
        }
      : undefined;

    const handleDeleteSuccess = (result?: WintSuperkollAPIModelWintSalary.SalaryMonthMutationResultBase) => {
      closeDialog?.();
      setTimeout(() => {
        onDeleteSuccess?.(result);
      });
    };

    return {
      isOpen: false,
      title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.DeleteDeviationQuestion),
      titleIcon: IconDefinitionConstants.WTrashAlt,
      actions: [
        {
          text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Delete),
          type: StyleEnums.DisplayType.Danger,
          successMessage: LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.SalaryDeviation.DeleteDeviationSuccess
          ),
        },
      ],
      hideCloseButton: true,
      dialogType: DialogEnums.DialogType.Content,
      children: (
        <DeleteForm
          deviationId={deviationId}
          openedPeriod={openedPeriod}
          selectedPeriod={selectedPeriodRange}
          onDeleteSuccess={handleDeleteSuccess}
          showAllDeleteOption={showAllDeleteOption}
        />
      ),
      type: StyleEnums.DisplayType.Danger,
      className: cssExports.dangerMessageStyle,
    };
  };

  const showDeleteConfirmation = (
    onDelete: () => Promise<any>,
    onContinueEditing?: () => Promise<any>,
    options?: {
      deviationId?: string;
      deviationType?: WintSalaryEnums.SalaryDeviationType;
      deviation?: any;
      dateRange?: DateRange;
      selectedPeriod?: Date;
      isFullDeviationLoaded?: boolean;
    },
    closeDialog?: () => void,
    onDeleteSuccess?: (result?: WintSuperkollAPIModelWintSalary.SalaryMonthMutationResultBase) => void
  ) => {
    const dateRange = options?.dateRange;
    const isWithinSingleMonth =
      dateRange?.start &&
      dateRange?.end &&
      dateRange.start.getFullYear() === dateRange.end.getFullYear() &&
      dateRange.start.getMonth() === dateRange.end.getMonth();

    const shouldShowDeleteForm =
      options?.deviationType !== undefined &&
      options?.deviation !== undefined &&
      dateRange !== undefined &&
      !isWithinSingleMonth &&
      options?.deviationId !== undefined &&
      SalaryDeviationHelper.shouldShowTimeDeviationSection(options.deviationType, options.deviation);

    if (shouldShowDeleteForm) {
      const selectedPeriodDate = options.selectedPeriod;
      dispatch(
        displayDialog(
          deleteFormDialog(
            options.deviationId!,
            options.dateRange!,
            selectedPeriodDate,
            options?.isFullDeviationLoaded,
            closeDialog,
            onDeleteSuccess
          )
        )
      );
    } else {
      dispatch(displayDialog(deleteConfirmationDialog(onDelete, onContinueEditing)));
    }
  };

  return { showDeleteConfirmation };
}

export default useDeleteConfirmation;
